import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import { BadgeCellRenderer } from '../../components/table-cell-renderers'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { ApplicationStatusEnum } from '../../utils/enums'
import general from '../../utils/general'

const ApplicationDetailModal = ({ isOpen, application, onClose, onShowStudentDetail, onShowAgentInfo }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)

  const activeApplication = details || application

  useEffect(() => {
    if (isOpen && application?.id) {
      setLoading(true)
      dispatch(
        ApiCallActions.Get({
          url: `applications/${application.id}`,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: ({ data }) => {
            const payload = data?.data || data || null
            setDetails(payload)
            setLoading(false)
          },
          onError: ({ errorMessage }) => {
            if (errorMessage) {
              general.notificationError(errorMessage)
            }
            setDetails(null)
            setLoading(false)
          }
        })
      )
    }

    if (!isOpen) {
      setDetails(null)
    }
  }, [dispatch, isOpen, application?.id])

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const getStatusBadge = () => {
    const statusValue = activeApplication?.applicationStatus || activeApplication?.status
    if (!statusValue) return null

    const statusMap = {
      [ApplicationStatusEnum.DRAFT]: { 
        label: t('APPLICATIONS_STATUS_DRAFT'), 
        class: 'badge-secondary',
        style: { backgroundColor: '#6c757d', color: '#fff' }
      },
      [ApplicationStatusEnum.SUBMITTED]: { label: t('APPLICATIONS_STATUS_SUBMITTED'), class: 'badge-info' },
      [ApplicationStatusEnum.UNDER_REVIEW]: { label: t('APPLICATIONS_STATUS_UNDER_REVIEW'), class: 'badge-warning' },
      [ApplicationStatusEnum.ACCEPTED]: { label: t('APPLICATIONS_STATUS_ACCEPTED'), class: 'badge-success' },
      [ApplicationStatusEnum.REJECTED]: { label: t('APPLICATIONS_STATUS_REJECTED'), class: 'badge-danger' },
      [ApplicationStatusEnum.WAITLISTED]: { label: t('APPLICATIONS_STATUS_WAITLISTED'), class: 'badge-warning' },
      [ApplicationStatusEnum.CANCELLED]: { 
        label: t('APPLICATIONS_STATUS_CANCELLED'), 
        class: 'badge-secondary',
        style: { backgroundColor: '#6c757d', color: '#fff' }
      }
    }

    const statusInfo = statusMap[statusValue.toLowerCase()] || { label: statusValue, class: 'badge-default' }
    return (
      <span 
        className={`badge ${statusInfo.class}`}
        style={statusInfo.style || {}}
      >
        {statusInfo.label}
      </span>
    )
  }

  const renderInfo = (label, value, icon) => {
    const displayValue = value === null || value === undefined || value === '' ? '-' : value

    return (
      <div className="m-b-15 d-flex">
        {icon && <i className={`zmdi ${icon} m-r-10 text-warning`} style={{ fontSize: '18px' }}></i>}
        <div>
          <small className="text-muted d-block">{label}</small>
          <strong>{displayValue}</strong>
        </div>
      </div>
    )
  }

  const getAgentName = (agent) => {
    if (!agent) return ''
    if (agent.companyName) return agent.companyName
    if (agent.name) return agent.name
    if (agent.fullName) return agent.fullName

    const firstName = agent.firstName || agent?.user?.firstName || ''
    const lastName = agent.lastName || agent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()

    if (combined) return combined

    return agent.email || agent?.user?.email || ''
  }

  if (!application) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('APPLICATIONS_DETAIL_TITLE')}
      subtitle={t('APPLICATIONS_DETAIL_SUBTITLE')}
      icon="zmdi-file-text"
      size="xlarge"
    >
      {loading ? (
        <div className="text-center p-4">
          <p>{t('APPLICATIONS_DETAIL_LOADING')}</p>
        </div>
      ) : (
        <div className="row clearfix">
          <div className="col-lg-4 col-md-5">
            <div className="card">
              <div className="body">
                <div className="m-b-15">
                  <small className="text-muted d-block">{t('APPLICATIONS_DETAIL_APPLICATION_LABEL')}</small>
                  <h5 className="m-b-0">#{activeApplication?.id?.substring(0, 8) || '-'}</h5>
                  <div className="d-flex align-items-center m-t-10" style={{ gap: '8px', flexWrap: 'wrap' }}>
                    {getStatusBadge()}
                  </div>
                </div>

                {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_APPLICATION_DATE'), formatDate(activeApplication?.applicationDate), 'zmdi-calendar')}
                {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_SUBMISSION_DATE'), formatDateTime(activeApplication?.submissionDate), 'zmdi-time')}
                {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_APPLICATION_YEAR'), activeApplication?.applicationYear, 'zmdi-calendar-check')}
                {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_APPLICATION_PERIOD'), activeApplication?.applicationPeriod, 'zmdi-calendar-note')}
              </div>
            </div>

            {activeApplication?.student && (
              <div className="card m-t-15">
                <div className="body">
                  <h6 className="m-b-15">{t('APPLICATIONS_DETAIL_SECTIONS_STUDENT')}</h6>
                  {onShowStudentDetail && activeApplication.student?.id ? (
                    <div className="m-b-10">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onShowStudentDetail(activeApplication.student)}
                      >
                        <i className="zmdi zmdi-eye m-r-5"></i>
                        {t('APPLICATIONS_DETAIL_VIEW_STUDENT')}
                      </button>
                    </div>
                  ) : null}
                  {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_STUDENT_ID'), activeApplication.student?.id, 'zmdi-account')}
                </div>
              </div>
            )}

            {activeApplication?.agent && (
              <div className="card m-t-15">
                <div className="body">
                  <h6 className="m-b-15">{t('APPLICATIONS_DETAIL_SECTIONS_AGENT')}</h6>
                  {onShowAgentInfo && activeApplication.agent?.id ? (
                    <div className="m-b-10">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onShowAgentInfo(activeApplication.agent)}
                      >
                        <i className="zmdi zmdi-eye m-r-5"></i>
                        {t('APPLICATIONS_DETAIL_VIEW_AGENT')}
                      </button>
                    </div>
                  ) : null}
                  {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_AGENT_ID'), activeApplication.agent?.id, 'zmdi-account-box')}
                  {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_AGENT_NAME'), getAgentName(activeApplication.agent), 'zmdi-accounts')}
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-8 col-md-7">
            <div className="card">
              <div className="body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="m-b-10">{t('APPLICATIONS_DETAIL_SECTIONS_PROGRAM')}</h6>
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_PROGRAM_NAME'), activeApplication?.program?.name, 'zmdi-graduation-cap')}
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_PROGRAM_NAME_EN'), activeApplication?.program?.nameEn, 'zmdi-graduation-cap')}
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_PROGRAM_ID'), activeApplication?.program?.id, 'zmdi-label')}
                  </div>
                  <div className="col-md-6">
                    <h6 className="m-b-10">{t('APPLICATIONS_DETAIL_SECTIONS_REVIEW')}</h6>
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_REVIEW_START_DATE'), formatDate(activeApplication?.reviewStartDate), 'zmdi-calendar-check')}
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_REVIEW_END_DATE'), formatDate(activeApplication?.reviewEndDate), 'zmdi-calendar-check')}
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_DECISION_DATE'), formatDate(activeApplication?.decisionDate), 'zmdi-check-circle')}
                  </div>
                </div>

                {activeApplication?.decisionNotes && (
                  <>
                    <hr />
                    <div className="row">
                      <div className="col-12">
                        <h6 className="m-b-10">{t('APPLICATIONS_DETAIL_SECTIONS_NOTES')}</h6>
                        <div className="card" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="body">
                            <p style={{ whiteSpace: 'pre-line', margin: 0 }}>
                              {activeApplication.decisionNotes}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <hr />

                <div className="row">
                  <div className="col-md-6">
                    <h6 className="m-b-10">{t('APPLICATIONS_DETAIL_SECTIONS_TIMESTAMPS')}</h6>
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_CREATED_AT'), formatDateTime(activeApplication?.createdAt), 'zmdi-time')}
                    {renderInfo(t('APPLICATIONS_DETAIL_FIELDS_UPDATED_AT'), formatDateTime(activeApplication?.updatedAt), 'zmdi-time')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ApplicationDetailModal

